import React from 'react';
import { useNavigate } from 'react-router';
import { ListMembersResponseElement, UserRoleEnum } from '../../../types/apiGeneratedTypes';
import TableRowActionMenu from '../../BaseComponents/TableRowActionMenu';
import { useDispatch, useSelector } from 'react-redux';
import {
  changeModalVisibility,
  setMemberDataToEdit,
  setMembersList
} from '../../../store/membersSlice';
import './MembersTable.scss';
import {
  setMemberIdToEdit,
  setMemberIdToToggle
} from '../../../store/membersSlice';
import {
  deactivateMember,
  activateMember,
  getMembersList,
  getDoctorsMembersList,
  getNutritionistsMembersList,
  updateMember,
  assignInChargeAdminToMember,
} from '../../../api/members';
import { formatUKDate } from '../../../utils';
import { RootState } from '../../../store/store';
import { toast } from 'react-toastify';
import Select, { SelectOption } from '../../BaseComponents/Select';
import FollowupStatusModal from './FollowupStatusModal';

const getMemberStatus = (memberData: ListMembersResponseElement): string => {
  const membershipStatus = (memberData as any).membership_status;
  if (membershipStatus === null || membershipStatus === undefined) {
    return '-';
  }
  return membershipStatus;
};

const formatStatusForDisplay = (status: string): string => {
  if (status === '-') {
    return '-';
  }
  return status.replace(/_/g, ' ').split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};


const membershipStatusOptions = [
  { key: 'all', value: 'All Statuses' },
  { key: 'user_added', value: 'User Added' },
  { key: 'user_paid', value: 'User Paid' },
  { key: 'member_active', value: 'Member Active' },
  { key: 'tuli_appt_booked', value: 'Tuli Appt Booked' },
  { key: 'tuli_appt_complete', value: 'Tuli Appt Complete' },
  { key: 'sample_received', value: 'Sample Received' },
  { key: 'report_available', value: 'Report Available' },
  { key: 'report_awaiting_review_by_gp', value: 'Report Awaiting Review By GP' },
  { key: 'report_awaiting_discussion', value: 'Report Awaiting Discussion' },
  { key: 'gp_review_booked', value: 'GP Review Booked' },
  { key: 'report_published', value: 'Report Published' },
  { key: 'nutritionist_appt_invite_sent', value: 'Nutritionist Appt Invite Sent' },
  { key: 'nutritionist_appt_booked', value: 'Nutritionist Appt Booked' },
  { key: 'care_notes_uploaded', value: 'Care Notes Uploaded' },
  { key: 'recommendations_uploaded', value: 'Recommendations Uploaded' },
  { key: 'supplements_uploaded', value: 'Supplements Uploaded' },
  { key: 'home_blood_appt_requested', value: 'Home Blood Appt Requested' },
  { key: 'home_blood_appt_booked', value: 'Home Blood Appt Booked' },
  { key: 'home_blood_appt_complete', value: 'Home Blood Appt Complete' },
  { key: 'scan_scheduled', value: 'Scan Scheduled' },
  { key: 'scan_complete', value: 'Scan complete' },
  { key: 'scan_review_invite_sent', value: 'Scan review invite sent' },
  { key: 'mri_scan_published', value: 'MRI Scan published' },
  { key: 'testing_complete_feedback', value: 'Testing complete feedback' },
  { key: 'pre_scan_consult_invite_sent', value: 'Pre-scan consultation invite sent' },
  { key: 'pre_scan_consult_booked', value: 'Pre-scan consultation booked' },
  { key: 'pre_scan_questionnaire_link_sent', value: 'Pre-scan questionnaire link sent' },
  { key: 'pre_scan_questionnaire_submitted', value: 'Pre-scan questionnaire submitted' },
  { key: 'subscription_cancelled', value: 'Subscription Cancelled' }
];

const followupStatusOptions: SelectOption[] = [
  { key: 'customer_contacted', value: 'Customer contacted' },
  { key: 'need_to_follow_up', value: 'Need to follow up' },
  { key: 'not_interested', value: 'Not interested' },
];

const getFollowupStatusLabel = (statusKey: string | null | undefined): string => {
  if (!statusKey) {
    return '-';
  }
  const option = followupStatusOptions.find((opt) => opt.key === statusKey);
  return option?.value || statusKey.replace(/_/g, ' ');
};

export type MembersTableRowProps = {
  memberData: ListMembersResponseElement;
  userRole?: UserRoleEnum;
  currentTab?:
  | 'active'
  | 'disabled'
  | 'free'
  | 'all'
  | 'custom-inqurries-blood'
  | 'custom-inqurries-body-scan';
  onMemberStatusChange?: () => void;
  inChargeAdminSelectOptions: SelectOption[];
  inChargeAdminsLoading: boolean;
};

const MembersTableRow = ({
  memberData,
  userRole,
  currentTab,
  onMemberStatusChange,
  inChargeAdminSelectOptions,
  inChargeAdminsLoading,
}: MembersTableRowProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isToggling, setIsToggling] = React.useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = React.useState(false);
  const [isUpdatingInCharge, setIsUpdatingInCharge] = React.useState(false);
  const [isUpdatingFollowup, setIsUpdatingFollowup] = React.useState(false);
  const [followupModalOpen, setFollowupModalOpen] = React.useState(false);
  const [pendingFollowupStatus, setPendingFollowupStatus] = React.useState<string | null>(null);
  const [localFollowupStatus, setLocalFollowupStatus] = React.useState<string | null | undefined>(
    undefined,
  );
  const [localFollowupComment, setLocalFollowupComment] = React.useState<string | null | undefined>(
    undefined,
  );

  const { admin: { role, is_super_admin } } = useSelector(
    (state: RootState) => state.admin
  );

  const canEditInCharge =
    role === UserRoleEnum.Admin ||
    role === UserRoleEnum.SuperAdmin ||
    is_super_admin === true;

  const getMembersForUpdate = async () => {
    if (userRole === UserRoleEnum.Doctor) {
      return await getDoctorsMembersList(0, 1000, '', 'with_doctor');
    } else if (userRole === UserRoleEnum.Nutritionist) {
      return await getNutritionistsMembersList(0, 1000, '', 'with_nutritionist');
    } else {
      return await getMembersList(0, 1000, '', 'with_doctor_and_nutritionist');
    }
  };

  const onOptionClick = async (id: string, action: string) => {
    if (action === 'edit') {
      dispatch(setMemberIdToEdit(id));
      dispatch(setMemberDataToEdit(memberData));
      dispatch(changeModalVisibility(true));
    }
    if (action === 'toggle') {
      dispatch(setMemberIdToToggle(id));
      setIsToggling(true);
      try {
        if (memberData.is_active) {
          await deactivateMember({ member_id: id });
        } else {
          await activateMember({ member_id: id });
        }
        if (onMemberStatusChange) {
          onMemberStatusChange();
        } else {
          const updatedList = await getMembersForUpdate();
          dispatch(setMembersList(updatedList.members));
        }
      } catch (error) {
        console.error('Error toggling member status:', error);
      } finally {
        setIsToggling(false);
      }
    } else {
      dispatch(setMemberIdToEdit(id));
      dispatch(setMemberDataToEdit(memberData));
    }
  };

  const onRowClick = (id: string, assigned: boolean | undefined) => {
    sessionStorage.setItem('assigned_doctor', JSON.stringify(assigned ?? false));
    navigate('/members/' + id);
  };

  const handleStatusSelect = async (option: SelectOption) => {
    if (isUpdatingStatus) return;

    const statusKey = option.key as string;
    if (!statusKey) return;

    setIsUpdatingStatus(true);

    try {
      await updateMember({
        user_id: memberData.id,
        membership_status: statusKey
      });

      toast.success('Member status updated successfully');


      if (onMemberStatusChange) {
        onMemberStatusChange();
      } else {
        const updatedList = await getMembersForUpdate();
        dispatch(setMembersList(updatedList.members));
      }
    } catch (error: any) {
      console.error('Error updating member status:', error);
      const errorMessage = error?.response?.data?.detail || error?.response?.data?.description || 'Failed to update member status';
      toast.error(errorMessage);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getSelectedStatusOption = (): SelectOption => {
    const currentStatus = getMemberStatus(memberData);
    if (currentStatus === '-') {
      return { key: '', value: '-' };
    }
    const option = membershipStatusOptions.find(opt => opt.key === currentStatus);
    return option || { key: currentStatus, value: formatStatusForDisplay(currentStatus) };
  };

  const getSelectedInChargeOption = (): SelectOption => {
    const assigned = memberData.in_charge_admin;
    if (assigned?.user_id) {
      const fromList = inChargeAdminSelectOptions.find(
        (o) => o.key === assigned.user_id
      );
      if (fromList) return fromList;
      return {
        key: assigned.user_id,
        value: `${assigned.first_name} ${assigned.last_name}`.trim(),
      };
    }
    return { key: '', value: '' };
  };

  const handleInChargeSelect = async (option: SelectOption) => {
    if (isUpdatingInCharge || inChargeAdminsLoading) return;
    const adminId = option.key as string;
    if (!adminId) return;

    setIsUpdatingInCharge(true);
    try {
      await assignInChargeAdminToMember({
        member_id: memberData.id,
        admin_id: adminId,
      });
      toast.success('In charge admin updated');
      if (onMemberStatusChange) {
        onMemberStatusChange();
      } else {
        const updatedList = await getMembersForUpdate();
        dispatch(setMembersList(updatedList.members));
      }
    } catch (error: any) {
      console.error('Error updating in charge admin:', error);
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.description ||
        'Failed to update in charge admin';
      toast.error(errorMessage);
    } finally {
      setIsUpdatingInCharge(false);
    }
  };

  const showAdditionalColumns =
    currentTab === 'active' ||
    currentTab === 'all' ||
    currentTab === 'custom-inqurries-blood' ||
    currentTab === 'custom-inqurries-body-scan' ||
    currentTab === 'free';
  const showBodyScanReasonColumn = currentTab === 'custom-inqurries-body-scan';
  const showFollowupStatusColumn =
    currentTab === 'custom-inqurries-blood' || currentTab === 'custom-inqurries-body-scan';
  const memberWithAdditionalFields = memberData as ListMembersResponseElement & {
    email?: string | null;
    number_of_blood_test_plan?: number | string | null;
    payment_amount?: number | string | null;
    subscription_creation_date?: string | null;
    created_at?: string | null;
    created_date?: string | null;
    scan_info?: {
      reason_for_scan?: string | null;
      desire_date?: string | null;
      postcode?: string | null;
      site_address?: string | null;
      location?: string | null;
      clinic_name?: string | null;
      clinic_address?: string | null;
    } | null;
    postcode?: string | null;
    post_code?: string | null;
    site_address?: string | null;
    site_adress?: string | null;
    location?: string | null;
    scan_location?: string | null;
    clinic_name?: string | null;
    clinic_address?: string | null;
    address_without_name?: string | null;
    address_line_1?: string | null;
    address_line_2?: string | null;
    city?: string | null;
    addons?: Array<{
      code?: string | null;
      name?: string | null;
      selection_type?: string | null;
      status?: string | null;
    }> | null;
    followup_status?: string | null;
    followup_comment?: string | null;
  };

  const currentFollowupStatus =
    localFollowupStatus !== undefined
      ? localFollowupStatus
      : memberWithAdditionalFields.followup_status ?? null;

  const currentFollowupComment =
    localFollowupComment !== undefined
      ? localFollowupComment
      : memberWithAdditionalFields.followup_comment ?? null;

  React.useEffect(() => {
    setLocalFollowupStatus(undefined);
    setLocalFollowupComment(undefined);
  }, [
    memberData.id,
    memberWithAdditionalFields.followup_status,
    memberWithAdditionalFields.followup_comment,
  ]);

  const getSelectedFollowupOption = (): SelectOption => {
    if (!currentFollowupStatus) {
      return { key: '', value: '-' };
    }
    const option = followupStatusOptions.find((opt) => opt.key === currentFollowupStatus);
    return option || { key: currentFollowupStatus, value: getFollowupStatusLabel(currentFollowupStatus) };
  };

  const handleFollowupSelect = (option: SelectOption) => {
    if (isUpdatingFollowup) {
      return;
    }
    const statusKey = option.key as string;
    if (!statusKey || statusKey === currentFollowupStatus) {
      return;
    }
    setPendingFollowupStatus(statusKey);
    setFollowupModalOpen(true);
  };

  const handleFollowupModalClose = () => {
    setFollowupModalOpen(false);
    setPendingFollowupStatus(null);
  };

  const handleFollowupConfirm = async (comment: string) => {
    if (!pendingFollowupStatus || isUpdatingFollowup) {
      return;
    }

    setIsUpdatingFollowup(true);
    try {
      await updateMember({
        user_id: memberData.id,
        followup_status: pendingFollowupStatus,
        followup_comment: comment,
      });
      setLocalFollowupStatus(pendingFollowupStatus);
      setLocalFollowupComment(comment);
      toast.success('Follow-up status updated successfully');
      setFollowupModalOpen(false);
      setPendingFollowupStatus(null);
      if (onMemberStatusChange) {
        onMemberStatusChange();
      }
    } catch (error: any) {
      console.error('Error updating follow-up status:', error);
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.description ||
        'Failed to update follow-up status';
      toast.error(errorMessage);
    } finally {
      setIsUpdatingFollowup(false);
    }
  };
  const numberOfTestsRequired = memberWithAdditionalFields.number_of_blood_test_plan;
  const paymentAmount = memberWithAdditionalFields.payment_amount;
  const subscriptionCreationDate = memberWithAdditionalFields.subscription_creation_date;
  const parsedCreatedAt = memberWithAdditionalFields.created_at
    ? new Date(memberWithAdditionalFields.created_at)
    : null;
  const requestedDate =
    memberWithAdditionalFields.scan_info?.desire_date ||
    (parsedCreatedAt && !Number.isNaN(parsedCreatedAt.getTime())
      ? parsedCreatedAt.toISOString()
      : memberWithAdditionalFields.created_date);
  const reasonForScan = memberWithAdditionalFields.scan_info?.reason_for_scan;
  const postcode =
    memberWithAdditionalFields.scan_info?.postcode ||
    memberWithAdditionalFields.postcode ||
    memberWithAdditionalFields.post_code;
  const siteAddress =
    memberWithAdditionalFields.scan_info?.site_address ||
    memberWithAdditionalFields.scan_info?.location ||
    memberWithAdditionalFields.scan_info?.clinic_address ||
    memberWithAdditionalFields.scan_info?.clinic_name ||
    memberWithAdditionalFields.site_address ||
    memberWithAdditionalFields.site_adress ||
    memberWithAdditionalFields.location ||
    memberWithAdditionalFields.scan_location ||
    memberWithAdditionalFields.clinic_address ||
    memberWithAdditionalFields.address_without_name ||
    memberWithAdditionalFields.clinic_name ||
    [
      memberWithAdditionalFields.address_line_1,
      memberWithAdditionalFields.address_line_2,
      memberWithAdditionalFields.city,
    ]
      .filter(Boolean)
      .join(', ');

  const addons = Array.isArray(memberWithAdditionalFields.addons)
    ? memberWithAdditionalFields.addons
    : [];

  const addonsTitle = (() => {
    if (addons.length === 0) return undefined;
    const parts = addons
      .map((a) => {
        const code = (a.code || '').trim();
        if (!code) return '';
        const st = (a.selection_type || '').toLowerCase();
        const testScope =
          st === 'recurring' ? 'All tests' : st === 'one_time' ? 'Single test' : '';
        const status = (a.status || '').trim();
        const statusSuffix = status && status.toLowerCase() !== 'active' ? ` (${status})` : '';
        return `${code}${testScope ? ` - ${testScope}` : ''}${statusSuffix}`;
      })
      .filter(Boolean);
    return parts.length > 0 ? parts.join('\n') : undefined;
  })();

  return (
    <div
      className={`table-row ${!memberData.is_active ? 'disabled' : ''}`}
      onClick={() => onRowClick(memberData.id, memberData?.is_assigned)}
      id={memberData.id}
    >
      <div className='name'>
        <span className='name-text'>{`${memberData?.first_name} ${memberData?.last_name}`}</span>
        {memberData?.tags && memberData.tags.length > 0 && (
          <div className='member-tags-badges'>
            {memberData.tags.map((tag) => (
              <span
                key={tag.id}
                className='member-tag-badge'
                style={{ backgroundColor: tag.color || '#238698' }}
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className='status' onClick={(e) => e.stopPropagation()}>
        {isUpdatingStatus ? (
          <span className='status-text'>Updating...</span>
        ) : (
          <div className={`status-select-wrapper status-select-${getMemberStatus(memberData).toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-')}`}>
            <Select
              name='membership_status'
              options={membershipStatusOptions}
              selectedOption={getSelectedStatusOption()}
              onChange={handleStatusSelect}
              placeholder={getMemberStatus(memberData) === '-' ? '-' : 'Select Status'}
              isError={false}
              openMenuToTop={false}
              maxHeight='300px'
            />
          </div>
        )}
      </div>
      <div className='memberId'>{`${memberData?.unique_id}`}</div>
      <div className='membership-type'>{memberData?.membership_type ? memberData.membership_type.charAt(0).toUpperCase() + memberData.membership_type.slice(1) : 'Core'}</div>
      {showFollowupStatusColumn && (
        <>
          <div className='followup-status' onClick={(e) => e.stopPropagation()}>
            {isUpdatingFollowup ? (
              <span className='status-text'>Updating...</span>
            ) : (
              <div
                className='followup-status-select-wrapper'
                title={currentFollowupComment || undefined}
              >
                <Select
                  name='followup_status'
                  options={followupStatusOptions}
                  selectedOption={getSelectedFollowupOption()}
                  onChange={handleFollowupSelect}
                  placeholder='-'
                  isError={false}
                  openMenuToTop={false}
                  maxHeight='200px'
                />
              </div>
            )}
          </div>
          <div className='followup-comment'>
            {currentFollowupComment?.trim() || '-'}
          </div>
        </>
      )}
      <div className='addons'>
        {addons.length === 0 ? (
          <span className='addons-empty'>-</span>
        ) : (
          <ul className='addons-list' title={addonsTitle}>
            {addons
              .filter((a) => (a?.code || '').trim())
              .map((a, idx) => {
                const code = (a.code || '').trim();
                const st = (a.selection_type || '').toLowerCase();
                const scope =
                  st === 'recurring' ? 'All tests' : st === 'one_time' ? 'Single test' : '';
                const status = (a.status || '').toLowerCase();
                const showStatusTag = status && status !== 'active';

                return (
                  <li key={`${code}-${idx}`} className='addons-item'>
                    <span className='addons-code'>{code}</span>
                    <span className='addons-tags'>
                      {scope ? (
                        <span
                          className={`addons-tag ${scope === 'All tests' ? 'addons-tag--all' : 'addons-tag--single'
                            }`}
                        >
                          {scope}
                        </span>
                      ) : null}
                      {showStatusTag ? (
                        <span className='addons-tag addons-tag--status'>
                          {status}
                        </span>
                      ) : null}
                    </span>
                  </li>
                );
              })}
          </ul>
        )}
      </div>
      <div className='doctor'>
        {memberData.doctor
          ? ` ${memberData.doctor?.first_name} ${memberData.doctor?.last_name}`
          : '-'}
      </div>
      <div className='nutritionist'>
        {memberData.nutritionist
          ? `${memberData.nutritionist?.first_name} ${memberData.nutritionist?.last_name}`
          : '-'}
      </div>
      {showAdditionalColumns && (
        <>
          <div className='email-id'>{memberWithAdditionalFields.email || '-'}</div>
          <div className='subscription-creation-date'>{formatUKDate(subscriptionCreationDate)}</div>
          <div className='payment-amount'>
            {paymentAmount !== null && paymentAmount !== undefined && paymentAmount !== ''
              ? String(paymentAmount)
              : '-'}
          </div>
          {showBodyScanReasonColumn ? (
            <>
              <div className='reason-for-scan'>{reasonForScan || '-'}</div>
              <div className='postcode'>{postcode || '-'}</div>
              <div className='site-address'>{siteAddress || '-'}</div>
              <div className='requested-date'>{formatUKDate(requestedDate)}</div>
            </>
          ) : (
            <div className='number-of-tests-required'>
              {numberOfTestsRequired !== null && numberOfTestsRequired !== undefined && numberOfTestsRequired !== ''
                ? String(numberOfTestsRequired)
                : '-'}
            </div>
          )}
        </>
      )}
      <div
        className='in-charge'
        onClick={canEditInCharge ? (e) => e.stopPropagation() : undefined}
      >
        {canEditInCharge ? (
          inChargeAdminsLoading ? (
            <span className='in-charge-placeholder'>—</span>
          ) : isUpdatingInCharge ? (
            <span className='status-text'>Updating...</span>
          ) : (
            <div className='in-charge-select-wrapper'>
              <Select
                name='in_charge_admin'
                options={inChargeAdminSelectOptions}
                selectedOption={getSelectedInChargeOption()}
                onChange={handleInChargeSelect}
                placeholder='-'
                isError={false}
                openMenuToTop={false}
                maxHeight='300px'
              />
            </div>
          )
        ) : (
          <span className='in-charge-readonly'>
            {memberData.in_charge_admin?.user_id
              ? `${memberData.in_charge_admin.first_name} ${memberData.in_charge_admin.last_name}`.trim()
              : '-'}
          </span>
        )}
      </div>
      {/* <div className='test-results'>
        <div className='table-count-cell'>2</div>
      </div>
      <div className='recommendations'>
        <div className='table-count-cell'>3</div>
      </div>

      <div className='appointments'>
        <div className='table-count-cell'>4</div>
      </div> */}
      <div className='exp-date'>
        {formatUKDate(memberData.subscription_end)}
      </div>
      <FollowupStatusModal
        isOpen={followupModalOpen}
        statusLabel={getFollowupStatusLabel(pendingFollowupStatus)}
        onClose={handleFollowupModalClose}
        onConfirm={handleFollowupConfirm}
        isSubmitting={isUpdatingFollowup}
      />
      <div className='actions'>
        {((role !== "doctor") || (memberData?.is_assigned && role === "doctor")) && <TableRowActionMenu
          options={[
            { key: 'edit', value: 'Edit' },
            {
              key: 'toggle',
              value: isToggling ? 'Updating...' : (memberData.is_active ? 'Disable' : 'Activate')
            }
          ]}
          id={memberData.id}
          onActionMenuClick={onOptionClick}
        />}
      </div>
    </div>
  );
};

export default MembersTableRow;
